"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, Sparkles, MapPin, UserCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "faq-1",
  question: "Why should a Bolton business invest in Local SEO specifically?",
  answer: "Local SEO targets high-intent local customers in Bolton who are actively searching for your exact services right now. Whether someone in Horwich is looking for a specialist or a business in Middlebrook needs local expertise, optimizing your search presence ensures you capture local market share before your competitors do."
}, {
  id: "faq-2",
  question: "How long does it take to rank in Google's Bolton Map Pack?",
  answer: "Most Bolton businesses I work with start seeing initial local movement within 60 to 90 days. Achieving sustainable top-3 rankings in the Google Local Pack typically takes 4 to 6 months of targeted local optimization, depending on your current site health and local competitor activity."
}, {
  id: "faq-3",
  question: "Do you optimize for specific townships around Bolton like Horwich and Farnworth?",
  answer: "Yes, absolutely. Local SEO is not limited to central Bolton (BL1). I build targeted local content strategies and geographical relevance signals for surrounding areas including Horwich, Farnworth, Westhoughton, Little Lever, Astley Bridge, and Blackrod."
}, {
  id: "faq-4",
  question: "Why work directly with you instead of a Manchester or Bolton SEO agency?",
  answer: "When you hire an agency, your project is frequently delegated to junior executives. When you work directly with me, you receive my personal hands-on technical expertise, custom-tailored strategy, direct communication, and transparent monthly reporting without agency overheads or account managers."
}, {
  id: "faq-5",
  question: "What is included in a complete Bolton Local SEO campaign with you?",
  answer: "My end-to-end Local SEO campaigns include full Google Business Profile optimization, local citation audit and cleanup, geotagged content optimization, technical site fixes, schema markup, and natural authority building across reputable regional directories."
}, {
  id: "faq-6",
  question: "How do you optimize my Google Business Profile for maximum local reach?",
  answer: "I perform a systematic overhaul: auditing primary and secondary categories, writing localized service descriptions, geotagging media assets, configuring products and services, setting up post updates, and establishing a structured customer review request process."
}, {
  id: "faq-7",
  question: "Can Local SEO help my Bolton business reach clients across Greater Manchester?",
  answer: "Yes. Once we establish a dominant local search foundation in Bolton, I structure your site architecture and landing pages to systematically expand your search footprint into neighboring hubs like Bury, Wigan, Blackburn, and Greater Manchester."
}, {
  id: "faq-8",
  question: "How do you handle local citations and NAP consistency across Lancashire?",
  answer: "Consistent Name, Address, and Phone number (NAP) data builds local trust with search engines. I audit all existing online references, resolve duplicate listings, and build high-quality local citations on trusted UK and Lancashire business registries."
}, {
  id: "faq-9",
  question: "Will I receive regular reporting on my Bolton local search rankings?",
  answer: "Yes, complete transparency is central to how I operate. Every month, you receive a detailed, easy-to-understand walk-through and report tracking Map Pack visibility, local organic traffic, lead attribution, and precise work completed."
}, {
  id: "faq-10",
  question: "Do I need a brand new website to rank locally in Bolton?",
  answer: "In most cases, no. I specialize in optimizing existing websites by refining internal linking, improving mobile speed, removing technical roadblocks, creating dedicated local service pages, and implementing structured schema markup."
}, {
  id: "faq-11",
  question: "How much does custom Local SEO for a Bolton business cost?",
  answer: "I offer customized, project-based retainers based strictly on your market goals and keyword competitiveness. Because you work directly with an independent consultant, every pound is invested directly into actual strategy and execution."
}, {
  id: "faq-12",
  question: "How do we get started with a free Bolton Local SEO consultation?",
  answer: "Simply book a free 30-minute SEO consultation through my contact form. I will review your current local search footprint in Bolton prior to our call and share actionable, high-impact recommendations with you."
}];
export default function Localseofaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseofaqbolton" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-semibold tracking-wide uppercase">
            <HelpCircle className="size-3.5 text-primary" />
            Bolton Local SEO FAQ
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground font-default tracking-tight leading-tight">
            Clear Answers for Your <span className="text-primary">Bolton Local SEO</span> Growth
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Got questions about ranking top of Google in Bolton? Here are clear, honest answers about how I deliver measurable local search growth when you work directly with me.
          </p>
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Consultant Sidebar Card */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
              <div className="flex items-center gap-4">
                <div className="size-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl shrink-0 shadow-xs">
                  YE
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">Yassine Essaadi</h3>
                  <p className="text-sm text-muted-foreground">Independent SEO Consultant</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <UserCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-foreground leading-snug">
                    <strong className="font-semibold text-foreground">Direct Consultant Access:</strong> You work 100% directly with me—never passed to account executives.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-foreground leading-snug">
                    <strong className="font-semibold text-foreground">Bolton Focus:</strong> Strategic local positioning tailored specifically for Bolton and Lancashire business ecosystems.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles className="size-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-foreground leading-snug">
                    <strong className="font-semibold text-foreground">No Agency Overhead:</strong> Custom-built campaigns focused on tangible revenue, Map Pack ranks, and leads.
                  </p>
                </div>
              </div>

              <Separator />

              <div className="pt-2 space-y-3">
                <p className="text-xs text-muted-foreground font-medium">Have a tailored query for your business?</p>
                <Button asChild size="lg" className="w-full justify-between group">
                  <Link to="/ContactPage">
                    <span>Book Free Consultation</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full justify-center">
                  <Link to="/LocalSEOPage">
                    <span>Explore Local SEO Services</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Accordion Column */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqList.map((faq, i) => <div data-index={i} key={faq.id}>
                  <motion.div variants={itemVariants}>
                    <AccordionItem value={faq.id} className="border border-border bg-card rounded-xl px-5 transition-all duration-200 hover:border-primary/40 shadow-2xs">
                      <AccordionTrigger className="text-primary font-semibold text-base sm:text-lg hover:no-underline py-5 text-left transition-colors">
                        <span className="flex items-start gap-3 pr-2">
                          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mt-0.5">
                            {i + 1}
                          </span>
                          <span className="leading-snug">{faq.question}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-foreground/80 text-sm sm:text-base leading-relaxed pb-6 pl-10 pr-2">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                </div>)}
            </Accordion>
          </motion.div>
        </div>

        {/* Bottom CTA Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-16 bg-muted/60 border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-2">
            <CheckCircle2 className="size-6" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
            Ready to Dominant Google Maps and Local Search in Bolton?
          </h3>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            Get a clear, direct review of your current local search ranking bottlenecks with zero sales pressure. Speak directly with Yassine today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto px-8">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-8">
              <Link to="/CaseStudiesPage">View Real Case Studies</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}